/**
 * The sidebar's module list, as a pure function of what this viewer is allowed to see.
 *
 * It lived inside `Layout`'s `useMemo`, where the only way to check it was to mount the whole
 * shell — every hook, every context, the widget. The rules are worth a test of their own:
 *
 *   * the finance-only advisor is EXCLUSIVE. Finance is their entire menu, nothing else renders,
 *     and the route guard bounces them off anything else;
 *   * `Roadmap` appears only while `prime_score_roadmap` is live for this viewer (16 Sep 2026).
 *     Its route redirects to `/diagnostic/results` when the flag is off, so a permanent entry
 *     would be a link to a redirect. It sits under Home because it is the same subject: the
 *     score Home summarises, and every lever behind it;
 *   * `Tracking` becomes the real finance cockpit when `xero_integration` is live, and stays a
 *     dimmed placeholder otherwise (`STUB_MODULES`, and `Layout`'s own stub rule);
 *   * `Author` follows Learn, for a viewer who may author;
 *   * with `speedies` on, Chat is its own entry at `/chat`, directly above Community, and Author
 *     leaves the menu (its URL still works).
 *
 * Flags are read by the CALLER and passed in, so this module has no cache and no clock in it.
 */
import { BarChart3, Map, MessageSquare } from 'lucide-react';
import {
  NavIconAlfieChat,
  NavIconCommunity,
  NavIconHome,
  NavIconLearn,
  NavIconLibrary,
  NavIconPeople,
  NavIconProjects,
  NavIconStreams,
  NavIconTracking,
} from '@/components/icons/NavIcons';

export interface NavModule {
  id: string;
  label: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

/** Modules that are not yet implemented — rendered dimmer in the sidebar. */
export const STUB_MODULES = new Set(['people', 'tracking', 'projects', 'streams']);

export const NAV_ITEMS: NavModule[] = [
  { id: 'today', label: 'Home', url: '/', icon: NavIconHome },
  { id: 'community', label: 'Community', url: '/community', icon: NavIconCommunity },
  { id: 'learn', label: 'Learn', url: '/learn', icon: NavIconLearn },
  { id: 'library', label: 'Library', url: '/assets', icon: NavIconLibrary },
  { id: 'people', label: 'People', url: '/team', icon: NavIconPeople },
  { id: 'tracking', label: 'Tracking', url: '/tracking', icon: NavIconTracking },
  { id: 'projects', label: 'Projects', url: '/crm/projects', icon: NavIconProjects },
  { id: 'streams', label: 'Streams', url: '/streams', icon: NavIconStreams },
];

export const ALFIE_ITEM: NavModule = {
  id: 'alfie',
  label: 'ALFIE Chat',
  url: '/alfie',
  icon: NavIconAlfieChat,
};

/** The finance-only advisor sees exactly this — their whole navigation. */
export const FINANCE_ITEM: NavModule = {
  id: 'finance',
  label: 'Finance',
  url: '/finance',
  icon: BarChart3 as unknown as React.ComponentType<{ className?: string }>,
};

/**
 * The Prime Score roadmap. The page carries the Results / Roadmap / History tabs, so this one
 * entry reaches all three; before it there was no way in from the navigation at all, only a
 * link on Home's coaching card (Davit, 16 Sep 2026).
 */
export const ROADMAP_ITEM: NavModule = {
  id: 'roadmap',
  label: 'Roadmap',
  url: '/diagnostic/roadmap',
  icon: Map as unknown as React.ComponentType<{ className?: string }>,
};

/** Chat as its own menu entry (Speedies, F-129). The chat view lives at /chat. */
export const CHAT_ITEM: NavModule = {
  id: 'chat',
  label: 'Chat',
  url: '/chat',
  icon: MessageSquare as unknown as React.ComponentType<{ className?: string }>,
};

export interface NavAudience {
  /** A finance-only advisor: Finance is the whole menu. */
  isAdvisor: boolean;
  /** `xero_integration` is live for this viewer: Tracking points at the finance cockpit. */
  xeroOn: boolean;
  /** `prime_score_roadmap` is live for this viewer: the Roadmap entry exists. */
  roadmapOn: boolean;
  /** `speedies` is on: Chat sits above Community at /chat. (Author is never in the menu: Tim, 3 Nov;
   *  `/author` still opens when typed.) */
  speediesOn?: boolean;
}

export function buildNavItems({
  isAdvisor,
  xeroOn,
  roadmapOn,
  speediesOn = false,
}: NavAudience): NavModule[] {
  if (isAdvisor) return [FINANCE_ITEM];
  const items = [...NAV_ITEMS];
  if (xeroOn) {
    const trackingIndex = items.findIndex((m) => m.id === 'tracking');
    if (trackingIndex !== -1) {
      items[trackingIndex] = { ...items[trackingIndex], url: '/finance' };
    }
  }
  if (roadmapOn) {
    const homeIndex = items.findIndex((m) => m.id === 'today');
    items.splice(homeIndex + 1, 0, ROADMAP_ITEM);
  }
  if (speediesOn) {
    const communityIndex = items.findIndex((m) => m.id === 'community');
    items.splice(communityIndex, 0, CHAT_ITEM);
  }
  return items;
}

/** The three diagnostic tabs the Roadmap entry reaches (`DiagnosticViewTabs`). */
const DIAGNOSTIC_TAB_PATH = /^\/diagnostic\/(results|roadmap|history)(\/|$)/;

/**
 * Tracking IS the finance module: it points at `/finance` once Xero is live and at the
 * `/tracking` placeholder before that, and the flag-off `/finance` routes still render (the
 * Finance Hub stub). So it owns both sections whichever URL it currently links to. Without
 * this, a `/finance` page with the flag off belonged to no entry and lit Home.
 */
const MODULE_SECTIONS: Record<string, readonly string[]> = {
  tracking: ['/tracking', '/finance'],
};

function inSection(path: string, base: string): boolean {
  return path === base || path.startsWith(`${base}/`);
}

/**
 * Which sidebar entry is lit for a path. The Roadmap entry reaches all three diagnostic tabs,
 * so it stays lit on Results and History too, not only on its own URL. Anything no entry claims
 * falls back to Home.
 */
export function activeModuleFor(pathname: string, items: readonly NavModule[]): string {
  const path = pathname.toLowerCase();
  if (path === '/') return 'today';
  if (path.startsWith('/alfie')) return 'alfie';
  if (path.startsWith('/settings')) return 'settings';
  if (DIAGNOSTIC_TAB_PATH.test(path) && items.some((m) => m.id === ROADMAP_ITEM.id)) {
    return ROADMAP_ITEM.id;
  }
  for (const item of items) {
    if (item.url === '/') continue;
    const sections = [item.url, ...(MODULE_SECTIONS[item.id] ?? [])];
    if (sections.some((base) => inSection(path, base))) return item.id;
  }
  return 'today';
}
